import {
  AutoScrollOptions,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { List } from '@nexelem/deprecated-theme';
import { ComponentProps, FC, useMemo } from 'react';
import { ActionsProps, SortableItemListProps, SortableListItem } from '../SortableListItem';

const defaultModifiers = [restrictToVerticalAxis, restrictToWindowEdges, restrictToFirstScrollableAncestor];

const defaultAutoscroll: AutoScrollOptions = {
  enabled: true,
  acceleration: 10,
};

export type SortableListProps<T> = Partial<
  Pick<ComponentProps<typeof DndContext>, 'autoScroll' | 'modifiers' | 'onDragStart' | 'onDragOver' | 'onDragMove'>
> & {
  readonly items: T[];
  readonly scrollAcceleration: number;
  onDragEnd(callback: (previousItems: T[]) => T[]): void;
  getActions(item: T, index: number): FC<ActionsProps>;
  Item?: FC<SortableItemListProps>;
  getDescription?: (item: T) => string;
};

/***
 * Important note: item field shouldn't be false
 * Using `id` is deprecated, use `field` instead
 */
export function SortableList<T extends Record<string, any> & { field: string | number | symbol; isHidden?: boolean }>({
  items,
  onDragEnd,
  modifiers,
  onDragStart,
  onDragOver,
  onDragMove,
  autoScroll,
  getActions,
  scrollAcceleration,
  getDescription = (item: T): string => item.field.toString(),
  Item,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 10,
        tolerance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 10,
        distance: 5,
      },
    }),
  );

  const Component: FC<SortableItemListProps> = useMemo(() => Item ?? SortableListItem, [Item]);

  const autoScrollConfig = useMemo<ComponentProps<typeof DndContext>['autoScroll']>(() => {
    if (typeof autoScroll === 'boolean') {
      return autoScroll;
    }
    if (scrollAcceleration) {
      return {
        ...defaultAutoscroll,
        acceleration: scrollAcceleration,
      };
    }
    return defaultAutoscroll;
  }, [autoScroll, scrollAcceleration]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onDragEnd((items) => {
        const oldIndex = items.map((_) => String(_.field)).indexOf(String(active.id));
        const newIndex = items.map((_) => String(_.field)).indexOf(String(over.id));

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <DndContext
      autoScroll={autoScrollConfig}
      collisionDetection={closestCenter}
      modifiers={modifiers || defaultModifiers}
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragMove={onDragMove}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
    >
      <SortableContext items={items.map((item) => String(item.field))}>
        <List className="pt-0">
          {items.map((item, index) => (
            <Component
              Actions={getActions(item, index)}
              description={getDescription(item)}
              id={String(item.field)}
              isHidden={item.isHidden}
              key={String(item.field)}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
}
